/**
 * The Perzo help center (www.perzo.art/help): short how-to articles, written from what the app does today. One
 * typed source for the public pages (web pages/site/Help*.tsx), the site registry (siteRoutes.ts: routes, sitemap,
 * llms.txt), the merchant app's "Guides" list and "?" links, and "Ask a question" (quotes passages verbatim,
 * shared/helpSearch.ts extractHelpAnswer).
 *
 * Writing rules: English, plain words, short steps. UI labels are copied from the app and written **like this**
 * (rendered bold; plain text for search and quotes). No prices or amounts: they come from the plan catalog
 * and are shown on the Plan & usage page. Billing text follows docs/architecture/COBRANCA-REGRAS.md. Change
 * `updated` whenever an article's text changes.
 */
import { SITE_ORIGIN } from './siteOrigin.js';

export type HelpBlock =
  | { kind: 'p'; text: string }
  | { kind: 'h'; text: string }
  | { kind: 'steps'; items: readonly string[] }
  | { kind: 'list'; items: readonly string[] }
  | { kind: 'tip'; text: string };

export type HelpCategory = 'setup' | 'styles' | 'products' | 'orders' | 'billing' | 'recipes' | 'support';

export interface HelpArticle {
  slug: string;
  category: HelpCategory;
  title: string;
  /** One sentence: the list card, the meta description (≤ 155 characters) and the search snippet. */
  summary: string;
  /** Extra search words merchants may type that the text does not use. */
  keywords: readonly string[];
  /** ISO date (YYYY-MM-DD) of the last text change. */
  updated: string;
  /**
   * The short video the owner records later. Until then the page shows a marked placeholder (never a fake embed).
   * `url`: an https video link once it exists.
   */
  video: { todo: string; url?: string };
  blocks: readonly HelpBlock[];
  related: readonly string[];
}

export const HELP_CATEGORY_LABELS: Record<HelpCategory, string> = {
  setup: 'Get set up',
  styles: 'Styles',
  products: 'Products and mockups',
  orders: 'Orders',
  billing: 'Plan and billing',
  recipes: 'Recipes',
  support: 'Support',
};

const UPDATED = '2026-10-17';

export const HELP_ARTICLES: readonly HelpArticle[] = [
  {
    slug: 'install-and-add-the-block',
    category: 'setup',
    title: 'Install Perzo and add the block to your theme',
    summary: 'Install the app, choose a plan, then add the Perzo personalizer block to your product page in the theme editor.',
    keywords: ['install', 'theme editor', 'app block', 'online store 2.0', 'dawn', 'not showing', 'hidden', 'product page', 'onboarding', 'guided setup'],
    updated: UPDATED,
    video: { todo: 'Screen recording: install, approve the plan in Shopify, add the block in the theme editor, open the product page.' },
    blocks: [
      { kind: 'p', text: 'Perzo works with Online Store 2.0 themes whose product page accepts app blocks, such as Dawn. Older (vintage) themes are not supported yet.' },
      {
        kind: 'steps',
        items: [
          'Install Perzo on your Shopify store and open it from your Shopify admin.',
          'Choose a plan on **Plan & usage** and approve it on the Shopify page. New stores get a free trial.',
          'Open **Guided setup** from the Overview. It walks you through each step and marks it done from your saved data.',
          'At the step **Add Perzo to your product page**, click **Open theme editor**. Shopify opens your product template with the **Perzo personalizer** block.',
          'Place the block above **Add to cart**, then click **Save** in the theme editor.',
          'Open your product page once. Perzo marks the step done when it sees the block on your store.',
        ],
      },
      { kind: 'tip', text: 'No theme editor link? Go to Shopify admin → Online Store → Themes → Customize, open a product template and add the **Perzo personalizer** block.' },
      { kind: 'h', text: 'The block does not show' },
      {
        kind: 'list',
        items: [
          'The block hides itself when the store has no active plan, or the product is not set up: no style published, personalization off, or no Shopify variant linked.',
          '**Guided setup** lists hidden product pages with the reason and a **Fix it** link.',
          'You can change the button text in the block settings (**Button label**). The default is “Personalize with your photo”.',
        ],
      },
    ],
    related: ['set-up-a-product', 'turn-on-cart-art', 'your-first-test-order'],
  },
  {
    slug: 'pick-a-style',
    category: 'styles',
    title: 'Pick a ready style or create your own',
    summary: 'Start from a ready style, or upload up to 4 images of the look you want and let Perzo Guide describe it in points you can edit.',
    keywords: ['style', 'ai style', 'starter', 'recipe', 'template', 'reference image', 'perzo guide', 'look', 'new style', 'watercolor', 'cartoon', 'cover'],
    updated: '2026-10-28',
    video: { todo: 'Screen recording: Ready styles gallery, Use this style, then New style with Upload art (read automatically).' },
    blocks: [
      { kind: 'p', text: 'A style is how the art looks. Shoppers pick one of your live styles in the wizard.' },
      { kind: 'h', text: 'Start from a ready style' },
      {
        kind: 'steps',
        items: [
          'Open **Styles** and click **Ready styles**.',
          'Filter by subject (**Pet**, **Person**, **Couple**, **Family**) and look at the before and after.',
          'Click **Use this style**. The style wizard opens at the **Look** step with the name filled in.',
          'Edit the points if you want, then click **Continue to test**.',
        ],
      },
      { kind: 'h', text: 'Create your own' },
      {
        kind: 'steps',
        items: [
          'In **Styles**, click **New style**.',
          '**Name your style**: shoppers see this name.',
          'In **Choose your starting point**, click **Upload art** to add reference art: up to 4 images of finished art in the style you want (not shopper photos). Use images you have the rights to. Or click **Browse styles** to start from a ready style.',
          'Perzo Guide reads your art by itself and describes the look as points: **What should the art look like?**, **Avoid**, **Always keep** and **Rules**. Edit any point. No art? Click **Or describe it in words**.',
          'Click **Continue to test**.',
        ],
      },
      { kind: 'tip', text: 'Perzo Guide is free: it never uses credits. It has a daily allowance, shown under the button.' },
      { kind: 'p', text: 'The wizard steps are **Name**, **Look**, **Test**, **Refine** and **Publish**. Next: test the style on real photos.' },
    ],
    related: ['test-a-style', 'set-up-a-product', 'recipe-pet-portrait-on-canvas'],
  },
  {
    slug: 'test-a-style',
    category: 'styles',
    title: 'Test a style before shoppers see it',
    summary: 'Test a style on typical shopper photos, refine it, and publish when results hold up. 1 test = 1 credit; failed tests are free.',
    keywords: ['test', 'credits', 'credit', 'sample photo', 'refine', 'publish', 'version', 'roll back', 'test bench', 'compare', 'cost of testing', 'failed'],
    updated: '2026-10-28',
    video: { todo: 'Screen recording: Test on sample photos, Use as is or Refine more, Publish style.' },
    blocks: [
      { kind: 'h', text: 'Test' },
      {
        kind: 'steps',
        items: [
          'In the style wizard, the **Test** step shows **Test on sample photos**.',
          'Pick a sample photo (**Pet**, **Person**, **Couple**) or click **Upload**. Use typical shopper photos, not only your best ones. Up to 4 photos per round.',
          'Click **Test on N photos**. Each photo shows beside its artwork; click **Zoom** to look closer, then **Review results**.',
        ],
      },
      {
        kind: 'tip',
        text: '1 test = 1 credit. Each finished test uses one generation credit from your plan, like a shopper’s art. Failed or stopped tests are never charged. Past your included credits, a test counts as an extra generation.',
      },
      { kind: 'h', text: 'Refine' },
      {
        kind: 'list',
        items: [
          'Switch between **Artwork** and **On product** to see the art on your mockup.',
          'Liked it? Click **Use as is**. Otherwise click **Refine more**, tell Perzo Guide what to change, or tap a quick change such as **Simpler background**.',
          'Click **Save vN and test**. Every save makes a new version; saved versions never change.',
        ],
      },
      { kind: 'h', text: 'Publish' },
      {
        kind: 'steps',
        items: [
          'Click **Use as is** to go to **Publish**.',
          'Pick the **Cover shoppers see** from your results.',
          'Click **Publish style**. Publishing unlocks after 1 finished test.',
          'Click **Add to a product** so shoppers can pick it.',
        ],
      },
      { kind: 'p', text: 'In the full studio you can also use the **Test bench** (**Run test generation**), compare versions and **Roll back** to an earlier one. Art already in orders never changes.' },
    ],
    related: ['pick-a-style', 'plan-and-usage', 'set-up-a-product'],
  },
  {
    slug: 'set-up-a-product',
    category: 'products',
    title: 'Set up a product: type and mockup',
    summary: 'Link a Shopify product, check its type (it sets the artwork’s shape) and pick a mockup photo. The print size is chosen when you download.',
    keywords: ['product', 'mockup', 'product type', 'shape', 'print size', 'dpi', 'bleed', 'variant', 'canvas', 'mug', 'pillow', 'poster', 't-shirt', 'print area', 'library', 'upload photo', 'shopper texts', 'name text'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Mockups, Set up a product: Products, Type, Styles (a Perzo style), Preview & save; then Customize for the Full editor.' },
    blocks: [
      { kind: 'p', text: 'In Perzo a product is a mockup: a product photo with its print area, its type, styles and texts. Shoppers see their art on it before they approve.' },
      { kind: 'h', text: 'Set up a product in four steps' },
      {
        kind: 'steps',
        items: [
          '**Products**: open **Mockups**, click **Set up a product** and tick the products shoppers will personalize (up to 10; for a product with sizes or options, pick one). Click **Next**.',
          '**Type**: Perzo picks each type (**Mug**, **Canvas**, **Poster**…) from the product’s name; the type sets the artwork’s shape. Click **Change** to pick another one, or **Portrait** / **Landscape** for wall art. No print size to enter: you choose it when you download the print file. Click **Next**.',
          '**Styles**: tick the styles shoppers can choose: your published styles and the **Perzo** ready styles (added to your store when you save). Or click **+ Create your own style**: you come back here with it selected once it is published. Click **Next**.',
          '**Preview & save**: Perzo shows each product with the sample art in place: your Shopify photo, or a Perzo sample scene. Check that it matches what you sell, then click **Save product** (or **Save N products**).',
        ],
      },
      { kind: 'tip', text: 'Your choices are kept on this browser while you set up: you can go back, reload or create a style and continue where you left off.' },
      { kind: 'h', text: 'Full editor (your own photo and placement)' },
      {
        kind: 'steps',
        items: [
          'On **Preview & save**, click **Customize** on a product. **Back to previews** returns with your changes.',
          'Under **Check your preview**, pick the **Product type**, then a photo (see below).',
          'Place the print area: drag it to move, pull a corner to resize (it keeps its shape), or use **Center**, **Fit**, **Straighten** and **Reset**. For a print seen at an angle, click **Edit perspective** and drag the four corners, then **Done**. Wait for **Placement OK**.',
          'Print-on-demand (Printful, Printify) needs an exact print size: click **Use an exact print size instead**. **Custom size** has the sizes, including **Wrap 8.5 × 3.5 in** for a mug, and **Width** and **Height** in inches or centimeters; **Size by product type instead** goes back. **Minimum DPI** (150 for canvas, 300 for small, close-viewed prints), **Bleed** and **Production time** are under **Advanced print settings**.',
          'Click **Save personalization** (**Save mockup** on a product already set up). Name, **Personalization active** and **Shopper texts** are under **More settings**.',
        ],
      },
      { kind: 'h', text: 'Where the mockup photo comes from' },
      {
        kind: 'list',
        items: [
          '**Your product photos**: the linked Shopify product’s photos, shown first. Use a photo without a design on it.',
          '**Sample scenes**: example photos with the print area placed. Pick one that looks like your product.',
          '**Upload**: **Upload product photo**, a photo of the blank product, shot straight on in even light.',
        ],
      },
      { kind: 'p', text: 'Library scenes today: frames and canvas, posters, mugs, pillows, T-shirts and a puzzle.' },
      { kind: 'tip', text: 'The side panel shows the **Estimated quality**: **Sharp at this size**, **Printable after enlargement**, or **This size needs more detail** with the largest size that works. The DPI numbers are in its tooltip.' },
      { kind: 'p', text: 'Optional: **Shopper texts** → **Add a text** lets shoppers add a name or dedication, set in your font on the print file.' },
    ],
    related: ['install-and-add-the-block', 'recipe-pet-portrait-on-canvas', 'recipe-christmas-photo-gift'],
  },
  {
    slug: 'turn-on-cart-art',
    category: 'setup',
    title: 'Turn on cart art',
    summary: 'The optional Perzo cart art app embed shows the shopper’s art as the picture of personalized lines in the cart.',
    keywords: ['cart', 'cart drawer', 'cart page', 'app embed', 'thumbnail', 'line item image', 'cart picture'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Turn on cart art link, the app embed switch in the theme editor, a cart with the art.' },
    blocks: [
      { kind: 'p', text: 'Cart art is optional. When it is on, the cart drawer and cart page show the shopper’s art instead of the product photo on personalized lines. Checkout and order emails keep the variant photo.' },
      {
        kind: 'steps',
        items: [
          'Open **Guided setup**, step **Add Perzo to your product page**.',
          'Click **Turn on cart art**. The theme editor opens on **App embeds**.',
          'Switch on **Perzo cart art** and click **Save**.',
          'Optional: turn on **Show “Your art is attached”** and edit the **Note text**.',
        ],
      },
      { kind: 'tip', text: 'No link? In the theme editor, open **App embeds** in the left sidebar and switch on **Perzo cart art**.' },
    ],
    related: ['install-and-add-the-block', 'your-first-test-order'],
  },
  {
    slug: 'your-first-test-order',
    category: 'setup',
    title: 'Your first test order',
    summary: 'Personalize your product like a shopper, check out once, and see the order with its print file in Perzo.',
    keywords: ['test order', 'shopper test', 'checkout', 'preview the wizard', 'try it', 'go live', 'live', 'make test art'],
    updated: UPDATED,
    video: { todo: 'Screen recording: product page, Personalize with your photo, Create my art, add to cart, checkout, the order in Perzo.' },
    blocks: [
      { kind: 'p', text: 'The last two steps of **Guided setup** are **Make test art on your store** and **Place a test order**. They use plan credits, like a real shopper.' },
      {
        kind: 'steps',
        items: [
          'Click **Open your store** and go to the product you set up.',
          'Click the Perzo button (default “Personalize with your photo”), then **Upload your photo**.',
          'Frame the photo, pick a style and click **Create my art**.',
          'Add the art you like to the cart and check out.',
          'In Perzo, open **Orders**. The order shows the original photo, the approved art and the print file.',
        ],
      },
      { kind: 'tip', text: 'Test orders show up in **Orders**. Cancel them in Shopify afterwards.' },
      { kind: 'p', text: 'On www.perzo.art you can also click **Preview the wizard** to try the shopper flow without your store. Successful generations there use your plan credits.' },
      { kind: 'p', text: 'When every step is done, Overview shows **You’re live**.' },
    ],
    related: ['download-print-files-and-correct-an-order', 'turn-on-cart-art', 'plan-and-usage'],
  },
  {
    slug: 'download-print-files-and-correct-an-order',
    category: 'orders',
    title: 'Download print files and correct an order',
    summary: 'Download the print file as PNG, JPEG, PDF or TIFF at the print size you choose, rebuild it, or replace the art on an order. Old versions are kept.',
    keywords: ['print file', 'download', 'zip', 'pdf', 'tiff', 'png', 'jpeg', 'production file', 'rebuild', 'replace art', 'fix order', 'wrong art', 'customer asked', 'reprint', 'fulfill', 'print on demand'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Orders, Print file…, Choose print size, Download ZIP, order page, Rebuild print file, Replace art.' },
    blocks: [
      { kind: 'h', text: 'Download' },
      {
        kind: 'list',
        items: [
          'In **Orders**, each row has **Download the print file** and **Print file…**: choose **PNG**, **JPEG**, **PDF** or **TIFF**. A product with an exact print size downloads at that size (the PDF is a page of the print size).',
          'A product set up by its type has no print size yet. **Original resolution** keeps the approved pixels. **Choose print size** makes the file at the **Width** you enter (the height follows the artwork’s shape) and shows its DPI; a size too large for the art is refused. A PDF needs a print size. The approved art never changes.',
          'Select rows to download several at once with **Download ZIP** (up to 100 items). Check **Shopper photos** to include them.',
          'Downloaded files are marked **Downloaded**; filter with **Print file**.',
          'Files are made without the watermark shoppers see before buying.',
        ],
      },
      { kind: 'p', text: 'Perzo has no direct print-on-demand integration yet: send the file to your printer with the order details.' },
      { kind: 'h', text: 'Correct an order' },
      { kind: 'p', text: 'Open the order. Under the item there are two buttons:' },
      {
        kind: 'list',
        items: [
          '**Rebuild print file**: same art, new file. No AI, no credit.',
          '**Replace art**: upload a new image (**Upload image**) or use art made in **Customer art** (**From Create art**). Write a **Reason**; it is kept in the activity log. Check **Add the customer’s texts** to set their texts on it.',
        ],
      },
      { kind: 'tip', text: 'The customer-approved art stays as version 1, and no version is overwritten. Art you make in **Customer art** uses credits like any art.' },
      { kind: 'p', text: 'Perzo warns you before a change when the file was already downloaded or the item is in production, done or shipped. Edited items show **Edited** in **Orders**.' },
    ],
    related: ['your-first-test-order', 'set-up-a-product', 'contact-support'],
  },
  {
    slug: 'plan-and-usage',
    category: 'billing',
    title: 'Plan and usage: what counts, alerts and cancelling',
    summary: 'What uses a credit, what is free, the overage cap, usage alerts, and how to change plan or cancel.',
    keywords: ['billing', 'plan', 'price', 'pricing', 'credits', 'generation', 'overage', 'extra generations', 'spend cap', 'cap', 'alert', 'email alert', 'trial', 'free trial', 'cancel', 'uninstall', 'change plan', 'downgrade', 'upgrade', 'reinstall', 'usage statement', 'charge', 'invoice', 'cost'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Plan & usage page, the usage bar, Extra generations with a cap, Usage statement.' },
    blocks: [
      { kind: 'p', text: 'Plans are billed through Shopify. The page is **Plan & usage** in Shopify admin (**Usage & billing** on www.perzo.art). Plan prices and the price of an extra generation are shown there.' },
      { kind: 'h', text: 'What counts' },
      {
        kind: 'list',
        items: [
          'Each finished artwork uses 1 credit, whether or not it is ordered: shopper art, your Studio tests, and art you make in **Customer art**.',
          'Regenerating counts: each new artwork is a new generation.',
          '**Compare N options** on the test bench counts as one test.',
        ],
      },
      { kind: 'h', text: 'What is free' },
      {
        kind: 'list',
        items: [
          'Failed or stopped generations: the credit is given back and never charged.',
          'A retry of the same request after a network error.',
          'Perzo Guide advice.',
          '**Rebuild print file** on an order.',
        ],
      },
      { kind: 'h', text: 'Order of use' },
      {
        kind: 'p',
        text: 'Included credits first, then bonus credits, then extra generations if you allow them. Included credits are per 30-day Shopify billing cycle. If you switch plans, the new plan’s included credits start in full.',
      },
      { kind: 'h', text: 'Extra generations (overage)' },
      {
        kind: 'steps',
        items: [
          'Extra generations are off by default. Without them, new art pauses when your included and bonus credits run out.',
          'To turn them on, open **Extra generations** (**Spend controls** on www.perzo.art), switch on **Allow overage** and set an **Overage cap per billing cycle** above zero.',
          'Check **I agree to overage charges up to this cap.** and click **Save spend controls**.',
        ],
      },
      {
        kind: 'tip',
        text: 'Overage is charged only if you allow it, never above your cap. At your cap, new generations pause until the next billing cycle. Switching plans in the middle of a cycle does not reset the cap. No extra charges during the trial.',
      },
      { kind: 'h', text: 'Free trial' },
      {
        kind: 'p',
        text: 'New stores get a free trial. It has a daily limit of generations per 24 hours, and its own included credits. When trial credits run out, new art resumes when the paid billing cycle starts. Shopify decides whether a reinstall gets a new trial.',
      },
      { kind: 'h', text: 'Alerts' },
      {
        kind: 'list',
        items: [
          'Overview shows a banner at 80% of your included credits, when they are used up, at 80% of your overage cap and when the cap is reached.',
          'The same four alerts are also sent to you by email.',
          '**Usage statement** lists every generation that used a credit. **Export CSV** downloads it.',
        ],
      },
      { kind: 'h', text: 'Change plan or cancel' },
      {
        kind: 'list',
        items: [
          'Change plan under **Plans** on the same page. You approve the change on the Shopify page.',
          'To cancel, use **Cancel plan** on Plan & usage: Perzo leaves your storefront right away, Shopify credits the unused days, and you can pick a plan again anytime. Or uninstall Perzo in Shopify admin: Settings > Apps > Perzo > Uninstall.',
          'Your styles, history and bonus credits stay until Shopify asks Perzo to delete your store’s data, usually about 48 hours after you uninstall. Reinstall before that and they are kept.',
        ],
      },
    ],
    related: ['test-a-style', 'contact-support'],
  },
  {
    slug: 'recipe-pet-portrait-on-canvas',
    category: 'recipes',
    title: 'Recipe: pet portrait on canvas',
    summary: 'A pet portrait style, a canvas product with a 16 × 20 in print, the Canvas on a wall mockup and the pet’s name as text.',
    keywords: ['pet', 'dog', 'cat', 'pet portrait', 'canvas', 'frame', 'wall art', 'pet name', 'recipe', 'example'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Illustrated pet ready style, canvas mockup from the library, pet name text, a test order.' },
    blocks: [
      { kind: 'p', text: 'What you need: a canvas product in Shopify, with one variant per size.' },
      {
        kind: 'steps',
        items: [
          'Style: in **Styles** → **Ready styles**, filter **Pet** and click **Use this style** on a pet style such as **Illustrated pet** or **Line art pet**. Or create your own from images of pet art you like.',
          'Test it on varied pet photos: dark and light coats, cats and dogs, close-ups and full body. Publish when each result is clearly the pet.',
          'Product: in **Mockups**, click **Set up** next to your canvas variant.',
          '**Artwork size**: pick **16 × 20 in** (or your canvas size). **Minimum DPI**: 150.',
          '**Mockup**: **Product type** **Frame / Canvas**, then **Sample scenes** → **Canvas on a wall**, or upload a photo of your blank canvas.',
          'Optional: **Shopper texts** → **Add a text** with the label “Pet’s name”.',
          'Check your pet style under **Styles on this product** and click **Save personalization**.',
          'Place a test order and check the print file.',
        ],
      },
      { kind: 'tip', text: 'Tell shoppers what photo to send: one pet, face visible, eyes in focus, daylight, no heavy filters.' },
    ],
    related: ['set-up-a-product', 'test-a-style', 'your-first-test-order'],
  },
  {
    slug: 'recipe-christmas-photo-gift',
    category: 'recipes',
    title: 'Recipe: Christmas photo gift',
    summary: 'A holiday gift with the Cozy Christmas ready style on a poster, mug or pillow, with your own order-by date.',
    keywords: ['christmas', 'holiday', 'gift', 'ornament', 'xmas', 'seasonal', 'mug', 'pillow', 'poster', 'cozy christmas', 'order by date', 'deadline', 'recipe'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Cozy Christmas ready style, a mug and a pillow mockup from the library, the order-by date on the product page.' },
    blocks: [
      { kind: 'p', text: '**Sample scenes** has no ornament scene. For an ornament, upload your own photo of the blank ornament. Sample scenes has posters, mugs and pillows ready.' },
      {
        kind: 'steps',
        items: [
          'Style: in **Styles** → **Ready styles**, click **Use this style** on **Cozy Christmas** (marked Christmas). Test it on family, couple and pet photos.',
          'Pick one or two products. In **Mockups**, click **Set up** next to each one.',
          'Mug: **Artwork size** **Wrap 8.5 × 3.5 in** under **Custom size** (an 11 oz mug’s full wrap; check your printer’s template), library scene **White mug, pet corner** or **White mug, breakfast table**.',
          'Pillow: **Pillow 18 × 18 in**, scene **Pillow on a gray sofa** or **Pillow on a beige sofa**.',
          'Poster: your poster size, scene **Poster on a living-room wall** or **Poster on a desk**.',
          'Check **Cozy Christmas** under **Styles on this product** and save.',
        ],
      },
      { kind: 'tip', text: 'Set your own order-by date from your production time, shipping times and your printer’s holiday cutoffs, and show it on the product page. Perzo does not show shoppers delivery dates.' },
      { kind: 'p', text: 'You can set **Production time** per product (working days) and holidays in **Settings**; they power the due date in **Orders**.' },
    ],
    related: ['set-up-a-product', 'recipe-pet-portrait-on-canvas', 'test-a-style'],
  },
  {
    slug: 'contact-support',
    category: 'support',
    title: 'Contact support',
    summary: 'You talk to Olavo, Perzo’s founder. Replies within 1 business day (US hours), in the app and by email.',
    keywords: ['support', 'help', 'contact', 'email', 'olavo', 'bug', 'problem', 'idea', 'feature request', 'setup call', 'talk to someone', 'human'],
    updated: UPDATED,
    video: { todo: 'Screen recording: Help & support, Talk to us form, a reply in the conversation.' },
    blocks: [
      { kind: 'p', text: 'You talk to Olavo, Perzo’s founder. Replies within 1 business day (US hours).' },
      {
        kind: 'steps',
        items: [
          'Open **Help & support** (**Support** in the Shopify admin menu).',
          'Under **Talk to us**, choose **Problem** or **Idea** and write a **Subject** and a **Message**.',
          'Optional: **Add context** with the style, product or order it is about.',
          'Click **Send**. Replies show in **Your conversations** and in your email.',
        ],
      },
      { kind: 'tip', text: 'Don’t include customer photos, personal details, passwords or API keys.' },
      { kind: 'p', text: 'Email works too: support@perzo.art.' },
      { kind: 'p', text: 'When it is offered on the page, **Book a 20-min setup call**: Olavo sets up your first product with you.' },
    ],
    related: ['install-and-add-the-block', 'plan-and-usage'],
  },
];

export const HELP_INDEX_PATH = '/help';

export function helpPath(slug: string): string {
  return `${HELP_INDEX_PATH}/${slug}`;
}

/** The article's public address (the merchant app links here, in a new tab). */
export function helpUrl(slug: string): string {
  return `${SITE_ORIGIN}${helpPath(slug)}`;
}

export function helpArticle(slug: string): HelpArticle | null {
  return HELP_ARTICLES.find((a) => a.slug === slug) ?? null;
}

/** `**Label**` markers removed. */
export function plainHelpText(text: string): string {
  return text.replace(/\*\*/g, '');
}

/** The article as plain text: what search indexes; every quoted passage is a substring of it. */
export function helpArticleText(article: HelpArticle, opts: { header?: boolean } = {}): string {
  const lines: string[] = [];
  if (opts.header !== false) lines.push(article.title, article.summary);
  for (const b of article.blocks) {
    if (b.kind === 'steps') b.items.forEach((item, i) => lines.push(`${i + 1}. ${plainHelpText(item)}`));
    else if (b.kind === 'list') b.items.forEach((item) => lines.push(`- ${plainHelpText(item)}`));
    else if (b.kind === 'tip') lines.push(`Tip: ${plainHelpText(b.text)}`);
    else lines.push(plainHelpText(b.text));
  }
  return lines.join('\n');
}

/** The article for each Guided setup step (the "?" link next to a step). */
export const SETUP_STEP_HELP: Readonly<Record<string, string>> = {
  plan: 'plan-and-usage',
  style: 'pick-a-style',
  product: 'set-up-a-product',
  block: 'install-and-add-the-block',
  test_art: 'your-first-test-order',
  test_order: 'your-first-test-order',
};

/** One quotable piece of an article: a step, a list item, a tip or one sentence of a paragraph. */
export interface HelpPassage {
  /** `<slug>#<block>.<part>`: stable while the article text does not change. */
  id: string;
  slug: string;
  /** Verbatim plain text, as in helpArticleText (labels without the ** markers). */
  text: string;
  /** The subheading it sits under, if any. */
  section: string | null;
}

/** The article split into passages, in reading order. */
export function helpPassages(article: HelpArticle): HelpPassage[] {
  const out: HelpPassage[] = [];
  let section: string | null = null;
  article.blocks.forEach((b, bi) => {
    const add = (text: string, part: number) => out.push({ id: `${article.slug}#${bi}.${part}`, slug: article.slug, text, section });
    if (b.kind === 'h') section = b.text;
    else if (b.kind === 'steps' || b.kind === 'list') b.items.forEach((item, i) => add(plainHelpText(item), i));
    else if (b.kind === 'tip') add(plainHelpText(b.text), 0);
    else plainHelpText(b.text).split(/(?<=[.!?])\s+(?=[A-Z“"])/).forEach((sentence, i) => add(sentence, i));
  });
  return out;
}
